import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { uomAPI } from '@/services/api';

const UOMPage = () => {
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUOM, setSelectedUOM] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', symbol: '', decimal_places: 2 });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchUOMs = useCallback(async () => {
    try {
      const response = await uomAPI.list({ search });
      setUoms(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch UOMs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => { fetchUOMs(); }, [fetchUOMs]);

  const handleOpenDialog = (uom = null) => {
    if (uom) {
      setSelectedUOM(uom);
      setFormData({ code: uom.code, name: uom.name, symbol: uom.symbol, decimal_places: uom.decimal_places });
    } else {
      setSelectedUOM(null);
      setFormData({ code: '', name: '', symbol: '', decimal_places: 2 });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name || !formData.symbol) {
      toast({ title: 'Validation Error', description: 'Code, Name, and Symbol are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (selectedUOM) {
        await uomAPI.update(selectedUOM.id, formData);
        toast({ title: 'Success', description: 'UOM updated successfully' });
      } else {
        await uomAPI.create(formData);
        toast({ title: 'Success', description: 'UOM created successfully' });
      }
      setDialogOpen(false);
      fetchUOMs();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save UOM', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await uomAPI.delete(selectedUOM.id);
      toast({ title: 'Success', description: 'UOM deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchUOMs();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete UOM', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <Toaster />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Units of Measurement</h1>
          <p className="text-gray-500">Manage product measurement units</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-500 hover:bg-blue-600" data-testid="add-uom-btn">
          <Plus className="w-4 h-4 mr-2" /> Add UOM
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Search UOMs..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-200 text-gray-800" />
        </div>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : uoms.length === 0 ? (
            <div className="text-center py-12">
              <Scale className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No UOMs found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-500">Code</TableHead>
                  <TableHead className="text-gray-500">Name</TableHead>
                  <TableHead className="text-gray-500">Symbol</TableHead>
                  <TableHead className="text-gray-500">Decimals</TableHead>
                  <TableHead className="text-gray-500">Status</TableHead>
                  <TableHead className="text-gray-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uoms.map(uom => (
                  <TableRow key={uom.id} className="border-gray-200 hover:bg-white" data-testid={`uom-row-${uom.code}`}>
                    <TableCell className="font-medium text-gray-800">{uom.code}</TableCell>
                    <TableCell className="text-gray-600">{uom.name}</TableCell>
                    <TableCell className="text-blue-400 font-mono">{uom.symbol}</TableCell>
                    <TableCell className="text-gray-500">{uom.decimal_places}</TableCell>
                    <TableCell>
                      <Badge variant={uom.is_active ? 'default' : 'secondary'}>
                        {uom.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                          onClick={() => handleOpenDialog(uom)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => { setSelectedUOM(uom); setDeleteDialogOpen(true); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800">
          <DialogHeader>
            <DialogTitle>{selectedUOM ? 'Edit UOM' : 'Add UOM'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Code *</Label>
                <Input value={formData.code} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., KG" className="bg-gray-50 border-gray-300" disabled={!!selectedUOM} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Kilogram" className="bg-gray-50 border-gray-300" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Symbol *</Label>
                <Input value={formData.symbol} onChange={(e) => setFormData(p => ({ ...p, symbol: e.target.value }))}
                  placeholder="e.g., kg" className="bg-gray-50 border-gray-300" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Decimal Places</Label>
                <Input type="number" min="0" max="4" value={formData.decimal_places}
                  onChange={(e) => setFormData(p => ({ ...p, decimal_places: parseInt(e.target.value) || 0 }))}
                  className="bg-gray-50 border-gray-300" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-500 hover:bg-blue-600">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate UOM?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate "{selectedUOM?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UOMPage;
